import * as AccordionPrimitive from '@radix-ui/react-accordion';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import absencesEmptyImg from '@/assets/images/dashboard-tiles/3.webp';
import { AbsenceDateGroup } from '@/components/Dashboard/AbsenceDateGroup';
import { AbsenceDetailSheet } from '@/components/Dashboard/AbsenceDetailSheet';
import {
  AbsencesModal,
  type AbsenceDetailSelection,
} from '@/components/Dashboard/AbsencesModal';
import { countUnread } from '@/components/Dashboard/countUnread';
import { AbsencesIcon } from '@/components/Icons/DashboardIcons';
import { DashboardTile } from '@/components/Dashboard/DashboardTile';
import { EmptyTileState } from '@/components/Dashboard/EmptyTileState';
import { TileLoadingState } from '@/components/Dashboard/TileLoadingState';
import { TileSubscriptionPaywall } from '@/components/Dashboard/TileSubscriptionPaywall';
import { useEvaluationsSummary } from '@/components/Dashboard/hooks/useEvaluationsSummary';
import { DashboardTileType } from '@/types/components';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { NonAttendance } from '@/types/overview';
import { sortByDateDesc } from '@/utils/date';

const ABSENCES_LIMIT = 6;

export const AbsencesTile: React.FC = () => {
  const { t } = useTranslation();
  const hasSubscription = useHasFamilySubscription();
  const isMobile = useIsMobile();
  const [isListOpen, setIsListOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] =
    useState<AbsenceDetailSelection>();
  const { data, isLoading } = useEvaluationsSummary({
    enabled: hasSubscription,
  });
  const allGroups = data?.nonAttendances ?? [];
  const groups = sortByDateDesc(allGroups, ({ date }) => date).slice(
    0,
    ABSENCES_LIMIT,
  );
  const isEmpty = groups.length === 0;
  const hasOlderAbsences = allGroups.length > ABSENCES_LIMIT;
  const badgeCount = hasSubscription
    ? countUnread(allGroups.flatMap(({ nonAttendances }) => nonAttendances))
    : 0;

  const handleOpenList = () => {
    setSelectedDetail(undefined);
    setIsListOpen(true);
  };

  const handleCloseList = () => {
    setIsListOpen(false);
    setSelectedDetail(undefined);
  };

  const handleBack = useCallback(() => setSelectedDetail(undefined), []);

  const handleAbsenceClick = useCallback(
    (absence: NonAttendance, date: string) =>
      setSelectedDetail({ absence, date }),
    [],
  );

  const desktopSelectedDetail = !isMobile ? selectedDetail : undefined;
  const mobileSelectedDetail = isMobile ? selectedDetail : undefined;

  return (
    <>
      <DashboardTile
        title={t('home.dashboard.absences.title')}
        footerButtonLabel={t('home.dashboard.absences.viewOlder')}
        type={DashboardTileType.Absences}
        icon={AbsencesIcon}
        badgeCount={badgeCount}
        hideFooter={!hasSubscription || isLoading || !hasOlderAbsences}
        isCompact={!hasSubscription}
        onFooterButtonClick={handleOpenList}
      >
        {!hasSubscription && (
          <TileSubscriptionPaywall
            imageSrc={absencesEmptyImg}
            description={t('home.dashboard.absences.subscriptionEmpty')}
          />
        )}
        {hasSubscription && (
          <>
            {isLoading && <TileLoadingState />}
            {!isLoading && isEmpty && <EmptyTileState />}
            {!isLoading && !isEmpty && (
              <AccordionPrimitive.Root
                type="multiple"
                className="flex flex-col md:h-full md:min-h-0 md:overflow-y-auto md:overscroll-contain"
              >
                {groups.map((group, index) => (
                  <AbsenceDateGroup
                    key={index}
                    group={group}
                    value={`absences-${index}`}
                    onAbsenceClick={handleAbsenceClick}
                  />
                ))}
              </AccordionPrimitive.Root>
            )}
          </>
        )}
      </DashboardTile>
      {(isListOpen || desktopSelectedDetail !== undefined) && (
        <AbsencesModal
          isOpen
          selectedDetail={desktopSelectedDetail}
          onClose={handleCloseList}
          onBack={isListOpen && desktopSelectedDetail ? handleBack : undefined}
          onAbsenceClick={handleAbsenceClick}
        />
      )}
      <AbsenceDetailSheet
        detail={mobileSelectedDetail}
        onClose={() => setSelectedDetail(undefined)}
      />
    </>
  );
};
